import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useDocsConfigs, type DocsConfig } from '@/api/docs';
import { Empty, ListPage, PageLoading, Th, tableWrap, theadCls, useListState, type FilterDef } from '@/components/network/NetworkKit';
import { AGENT_TYPES, ExpiryChip, RmmCell, daysUntil } from '@/components/docs/DocsKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { companyPath, docsOrgPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Every IT Glue configuration across all organisations, each joined to its live Datto RMM device
 * (IT Glue's rmm-record → Datto numeric id → our device cache). Hygiene views: computers with no
 * Datto device, IT Glue records pointing at a device Datto no longer has, warranties lapsing.
 */

const isComputer = (c: DocsConfig) => AGENT_TYPES.test(c.type ?? '');
const FILTERS: FilterDef<DocsConfig>[] = [
  { id: 'active',   label: 'Active',              test: (c) => !c.archived },
  { id: 'no-rmm',   label: 'Computers not in Datto RMM', test: (c) => !c.archived && isComputer(c) && c.dattoDeviceId == null },
  { id: 'rmm-gone', label: 'Datto device gone',   test: (c) => !c.archived && c.dattoDeviceId != null && !c.rmm },
  { id: 'warranty', label: 'Warranty ending (90d)', test: (c) => { const d = daysUntil(c.warrantyExpires); return !c.archived && d != null && d >= 0 && d <= 90; } },
  { id: 'no-psa',   label: 'Not linked to an Autotask CI', test: (c) => !c.archived && c.autotaskCiId == null },
  { id: 'archived', label: 'Archived',            test: (c) => c.archived },
];

export default function DocsConfigurationsPage() {
  const { data, isLoading, error, isFetching } = useDocsConfigs({ scoped: true });
  const [type, setType] = useState('');
  const all = data?.items ?? [];
  const items = useMemo(() => (type ? all.filter((c) => c.type === type) : all), [all, type]);
  const types = useMemo(() => [...new Set(all.map((c) => c.type).filter((t): t is string => !!t))].sort(), [all]);
  const l = useListState('cosmos:docs:configs:show', FILTERS, items,
    (c) => `${c.name} ${c.orgName} ${c.companyName ?? ''} ${c.serial ?? ''} ${c.model ?? ''} ${c.contact ?? ''} ${c.ip ?? ''} ${c.rmm?.hostname ?? ''}`);
  const rows = l.rows.slice(0, 1000);

  return (
    <ListPage area="Docs" title="Configurations" count={data ? l.rows.length : null} isFetching={isFetching}
      subtitle={data && <>IT Glue configurations × Datto RMM · read {fmtRelative(data.fetchedAt)}</>}
      search={{ q: l.q, setQ: l.setQ, placeholder: 'Name, client, serial, contact, IP' }}
      actions={
        <select value={type} onChange={(e) => setType(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground">
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      }
      filters={{ defs: FILTERS, show: l.show, pick: l.pick, counts: l.counts }}>
      <PageLoading isLoading={isLoading} error={error} vendor="IT Glue configurations" />
      {data && (
        <>
          <div className={tableWrap}>
            <table className="w-full text-sm" style={{ minWidth: 1250 }}>
              <thead className={theadCls}>
                <tr><Th>Name</Th><Th>Client</Th><Th>Type</Th><Th>Make / model</Th><Th>Serial</Th><Th>Contact</Th><Th>Datto RMM</Th><Th>Autotask CI</Th><Th>Warranty</Th><Th /></tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {rows.length === 0 && <Empty cols={10} />}
                {rows.map((c) => (
                  <tr key={c.id} className={cn('hover:bg-muted/30', c.archived && 'opacity-60')}>
                    <td className="px-3 py-2"><div className="max-w-[260px] truncate font-medium text-foreground" title={c.name}>{c.name}</div>{c.os && <div className="max-w-[240px] truncate text-[11px] text-muted-foreground">{c.os}</div>}</td>
                    <td className="max-w-[220px] px-3 py-2 text-xs">
                      <Link to={docsOrgPath(c.orgId, 'configurations')} className="block truncate text-foreground hover:underline" title={c.orgName}>{c.companyName ?? c.orgName}</Link>
                      {c.companyId != null && <Link to={companyPath(c.companyId)} className="text-[11px] text-muted-foreground hover:underline">Orbit</Link>}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{c.type ?? '—'}</td>
                    <td className="px-3 py-2 text-xs"><div className="max-w-[200px] truncate" title={[c.manufacturer, c.model].filter(Boolean).join(' ')}>{[c.manufacturer, c.model].filter(Boolean).join(' ') || '—'}</div></td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground"><div className="max-w-[130px] truncate" title={c.serial ?? ''}>{c.serial ?? '—'}</div></td>
                    <td className="px-3 py-2 text-xs"><div className="max-w-[150px] truncate" title={c.contact ?? ''}>{c.contact ?? '—'}</div></td>
                    <td className="px-3 py-2"><RmmCell c={c} /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{c.autotaskCiId != null ? `#${c.autotaskCiId}` : <span className="italic">none</span>}</td>
                    <td className="px-3 py-2"><ExpiryChip date={c.warrantyExpires} /></td>
                    <td className="px-3 py-2 text-right">{c.url && <a href={c.url} target="_blank" rel="noreferrer" title="Open in IT Glue" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {l.rows.length > rows.length && <p className="mt-2 text-xs text-muted-foreground">Showing the first {rows.length} of {l.rows.length} — search or filter to narrow.</p>}
          <p className="mt-2 text-[11px] text-muted-foreground">
            "Computers" = types matching workstation, laptop, desktop, server or computer. The Datto RMM link comes from IT Glue's own Datto integration;
            "Device gone" means IT Glue still points at a Datto device that no longer exists.
          </p>
        </>
      )}
    </ListPage>
  );
}
